import { Component, Injector, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ClaimsBaseComponent } from "../../claim-base.component";
import { UiOptionsModel } from '../../../ui/ui-options.model';
import { TransactionInfo } from 'src/app/model/transaction-info.model';
import { TransactionInfoService } from 'src/app/services/transaction-info.service';
import { InsuredPerson } from 'src/app/model/insured-person.model';
import { UiComponent } from 'src/app/ui/ui.component';
import { takeUntil } from 'rxjs';
import {UtilitiesService} from "../../../utilities/utilities.service";

@Component({
    selector: 'app-paynow',
    templateUrl: './paynow.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PaynowComponent extends ClaimsBaseComponent implements OnInit {

    transactionInfo: TransactionInfo;
    claimantOptions: UiOptionsModel[] = [];
    payNowForm: FormGroup;
    isPolicyholderClaimant: boolean = false;
    @ViewChild('PAYNOW_CLAIMANT_ID', { static: false, read: UiComponent }) uiClaimantId: UiComponent;
    @ViewChild('PAYNOW_NRIC_ID_TXT', { static: false, read: UiComponent }) uiClaimantId2: UiComponent;
    

    constructor(private fb: FormBuilder,
        private transactionInfoService: TransactionInfoService,
        private injector: Injector) {
        super(injector);
        this.transactionInfo = this.transactionInfoService.getTransactionInfo();
    }

    ngOnInit() {
        //set the claimant if claim is generic.
        this.setClaimantDataForGenericClaim();
        //set the claimant id and name if not set.
        if (!this.isNotNullOrUndefinedStr(this.claim.getProxyName()) && !this.isNotNullOrUndefinedStr(this.claim.getProxyId())) {
            let payeeName = this.claim.getPolicyHolderName();
            let payeeId = null;

            if (this.transactionInfo.isTravelProduct() || this.transactionInfo.isAccidentProduct()) {
                payeeName = this.claim.getClaimantName();
                if (UtilitiesService.stripNonAlphaNumeric(this.claim.getClaimantIdNumber())) {
                    payeeId = this.claim.getClaimantIdNumber();
                }
            } else {
                if (UtilitiesService.stripNonAlphaNumeric(this.claim.getPolicyHolderId())) {
                    payeeId = this.claim.getPolicyHolderId();
                }
            }

            this.claim.setProxyName(payeeName);
            this.claim.setProxyId(payeeId);
        }

        this.payNowForm = this.fb.group(
            {
                payNowOption: [this.claim.getProxyIdType()],
                payNowClaimant: [this.claim.getProxyName()],
                payNowClaimantId: [this.claim.getProxyId()],
                payNowClaimantUENId: [this.claim.getPolicyHolderId()],
                payNowClaimantUENName: [this.claim.getPolicyHolderName()],
                payNowClaimantNRICName: [this.claim.getProxyName()],
                payNowClaimantNRICId: [this.claim.getProxyId()],
            });
    }

    ngAfterViewInit() {
        this.updatePaynowFormData();
        this.checkClaimantAdded();

        this.payNowForm.get('payNowOption').valueChanges.pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(data => {
                if(data !=null){
                    this.validatePayNowForm();
                }
            });

        setTimeout(()=>{
            this.checkedRadioForDefaultNRICOrUEN();
        },10);
    }

    updatePaynowFormData() {
        this.payNowForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            const payNowOptionValue = super.getValueInComponent('payNowOption');
            this.claim.setProxyIdType(payNowOptionValue);
            this.claim.setProxyIdTypeDesc(payNowOptionValue);

            if (this.claim.getProxyIdType() == "UEN") {
                this.claim.setProxyId(this.claim.getPolicyHolderId());
                this.claim.setProxyName(this.claim.getPolicyHolderName());
            } else if (this.claim.getProxyIdType() == "NRIC/FIN") {
                this.claim.setProxyId(this.claim.getClaimantIdNumber());
                this.claim.setProxyName(this.claim.getClaimantName());
            }
            this.checkClaimantAdded();
        });
        
        setTimeout(() => {
            this.setClaimantInDropdown();
        }, 10);
    }
    /**
     * Validates the PayNow form to ensure all required fields are correctly filled and meet the expected criteria.
     * 
     * @returns {boolean} - Returns `true` if the form is valid, otherwise throws an error or returns `false`.
     * 
     * @throws {Error} - Throws an error if the claimant ID or NRIC/FIN is invalid.
     * 
     * The validation logic includes:
     * - Checks if the form passes a base validation using `super.validateForm`.
     * - For policyholders of type 'C' with non-generic claims:
     *   - Validates the `payNowClaimantNRICId` field.
     *   - Ensures the NRIC/FIN is not null, undefined, or invalid.
     *   - Sets appropriate error messages and form states.
     * - For other cases:
     *   - Validates the `payNowClaimantId` field.
     *   - Ensures the claimant ID is not null, undefined, or invalid.
     * 
     * If any validation fails, an error message is displayed, and the function may throw an error.
     */
    validatePayNowForm(): boolean {
        
        let isValid = true;
        const nricValue2 = this.payNowForm.get('payNowClaimantId').value;
        if (this.claim.getPolicyHolderType()?.toLowerCase() === 'c' && !this.transactionInfo.isGenericClaim()) {
            const nricValue = this.payNowForm.get('payNowClaimantNRICId').value;
            if (!UtilitiesService.stripNonAlphaNumeric(nricValue) && this.claim.getProxyIdType()?.toUpperCase() == "NRIC/FIN") {
                this.uiClaimantId2?.setValue(null);
                this.uiClaimantId2?.error("invalidNRIC_FIN");
                this.uiClaimantId?.error(null);
                isValid = false
            } else {
                this.payNowForm.markAsUntouched();
                this.payNowForm.controls.payNowClaimantNRICId.setErrors(null);
                this.payNowForm.markAsPristine();
            }
        }
        else if (!UtilitiesService.stripNonAlphaNumeric(nricValue2)) {
            this.uiClaimantId?.setValue(null);
            this.uiClaimantId?.error("invalidNRIC_FIN");
            isValid = false;
        }

        if (!super.validateForm(this.payNowForm)) {
            return;
        }

        return isValid;
    }

    private static isInvalidTrimmedValue(value: string | null | undefined): boolean {
        const trimmedValue = value?.trim();
        return trimmedValue == "." || trimmedValue == "";
    }

    
    /**
     * Populates the claimant dropdown options with a list of claimants.
     * 
     * This method performs the following steps:
     * 1. Adds the policyholder as the first option in the dropdown by default.
     * 2. Iterates through the list of claimants retrieved from the claim object (`getClaimantList()`):
     *    - Adds each claimant to the dropdown options, excluding the policyholder.
     *    - Sets a flag (`isPolicyholderClaimant`) to `true` if the policyholder is found in the claimant list.
     * 3. Adds the claimant name from the claim object (`getClaimantName()`), 
     *    excluding it if it matches the policyholder name.
     * 
     * @remarks
     * - The `UiOptionsModel` is used to create dropdown options with both display and value set to the claimant's name.
     * - The `isPolicyholderClaimant` flag is used to track whether the policyholder is already present in the dropdown.
     */
    setClaimantInDropdown() {
        //add claimant from insured persons list.
        let insuredPersonsList = this.transactionInfo.getInsuredPersonsList();
        const self =this;
        insuredPersonsList.forEach(insuredPerson => {
            // check if insured already exists in the claimantoptions.
            let isExist = self.claimantOptions.some(
                claimant => claimant.value == insuredPerson.getInsuredName());
            if (isExist) return;
            const claimant = new UiOptionsModel(
                insuredPerson.getInsuredName(),
                insuredPerson.getInsuredName());
            self.claimantOptions.push(claimant);

        });
        

        // Add policyholder name from claimantList.(do not add if its policyholder)
        const claimantList = this.claim.getClaimantList();
        if (claimantList != null && claimantList.length != 0) {
            claimantList.forEach(data => {
                if (this.claim.getPolicyHolderName() != data.getClaimantName()) {
                    const claimant = new UiOptionsModel(
                        data.getClaimantName(),
                        data.getClaimantName());
                    this.claimantOptions.push(claimant);
                } else {
                    this.isPolicyholderClaimant = true;
                }
            });

        }
        // Add policyholder name from the claim object. (do not add if its policyholder)
        if (this.claim.getPolicyHolderName() != this.claim.getClaimantName()) {
            //check if claimantname is aready in the claimantList.
            let isExist = this.claimantOptions.some(
                claimant => claimant.value == this.claim.getClaimantName());
            if (isExist) return;
            const claimant = new UiOptionsModel(
                this.claim.getPolicyHolderName(),
                this.claim.getPolicyHolderName());
            this.claimantOptions.push(claimant);
        } else {
            this.isPolicyholderClaimant = true;
        }
    }


    /**
     * Sets the claimant data for a generic claim.
     * 
     * This method checks if the current transaction is a generic claim and if the claimant name
     * is undefined. If both conditions are met, it assigns the policy holder's name to both
     * the claimant name and the contact name of the claim.
     * 
     * Preconditions:
     * - `this.transactionInfo.isGenericClaim()` determines if the claim is generic.
     * - `this.claim.getClaimantName()` checks if the claimant name is undefined.
     * 
     * Postconditions:
     * - If the claim is generic and the claimant name is undefined, the claimant name and
     *   contact name are set to the policy holder's name.
     */
    setClaimantDataForGenericClaim(){
        //checking if the claim is generic.
        if(this.transactionInfo.isGenericClaim() && this.claim.getClaimantName() === undefined){
            this.claim.setProxyId(this.claim.getPolicyHolderId());
            this.claim.setProxyName(this.claim.getPolicyHolderName());
        }
    }

    /**
     * Sets the claimant ID and name based on the selected claimant from the dropdown.
     * 
     * This method retrieves the selected claimant's name from the event object and iterates through
     * the insured persons list to find a match. If a match is found, it updates the claimant ID and name
     * in the claim object and sets the corresponding form control value.
     * 
     * @param event - The event object containing the selected claimant's name.
     */
    claimantChange(event: any) {
        let insuredPersonsList = this.transactionInfo.getInsuredPersonsList();
        let self = this;
        //iterate insuredpersonlist to find the selected claimant.
        insuredPersonsList.some(insuredPerson => {
            if (event.target.value == insuredPerson.getInsuredName()) {
                self.payNowForm.get("payNowClaimantId").setValue(insuredPerson.getInsuredPersonNationalIdNumber());
                self.claim.setProxyId(insuredPerson.getInsuredPersonNationalIdNumber());
                self.claim.setProxyName(event.target.value);
            } 
        });
        this.validatePayNowForm();
    }

  
    /**
     * Checks if there are any manually added claimants in the claim.
     * This method retrieves a list of manually added insured persons
     * and verifies if the claim contains any of them.
     */
    checkClaimantAdded() {
        let lst: InsuredPerson[] = this.listManuallyAddedClaimant();
        this.isClaimContainsManuallyAddedInsured(lst);
    }

    
    /**
     * Retrieves a list of insured persons who have been manually added to the transaction.
     * These are identified as insured persons whose risk number is undefined.
     *
     * @returns {InsuredPerson[]} An array of insured persons without a defined risk number.
     */
    private listManuallyAddedClaimant(): any {
        let insuredPersonsList = this.transactionInfo.getInsuredPersonsList();
        let insuredLst: InsuredPerson[] = [];
        insuredPersonsList.forEach(insured => {
            if (insured.getRiskNumber() === undefined) {
                insuredLst.push(insured);
            }
        });
        return insuredLst;
    }

    /**
     * Checks if the claim contains any manually added insured persons.
     * 
     * This method iterates through the provided list of manually added insured persons
     * and compares their national ID numbers with the claimant's ID number and the ID numbers
     * of the claimants in the claim's claimant list. If a match is found, the claim is marked
     * as containing manually added insured persons.
     * 
     * @param manuallyAddedInsured - An array of `InsuredPerson` objects representing the manually added insured persons.
     */
    isClaimContainsManuallyAddedInsured(manuallyAddedInsured: InsuredPerson[]) {
        manuallyAddedInsured.forEach(newInsured => {
            if (this.claim.getProxyId() == newInsured.getInsuredPersonNationalIdNumber()) {
                this.claim.setManuallyAddedInsured(true);
                return;
            }

            for (const claimant of this.claim.getClaimantList()) {
                if (newInsured.getInsuredPersonNationalIdNumber() == claimant.getClaimantIdNumber()) {
                    this.claim.setManuallyAddedInsured(true);
                    return;
                }
            }
        });
    }

    isPaynowForGeneric(){
        return this.transactionInfo.isGenericClaim() && !this.transactionInfo.isMotorGenericSGP();
    }

    checkedRadioForDefaultNRICOrUEN(){
        if(this.claim.getPolicyHolderType().toLowerCase()==='c' && this.transactionInfo.isGenericClaim()){
            this.payNowForm.get('payNowOption').setValue('UEN');
        }else if((this.transactionInfo.isTravelPersonal() || this.transactionInfo.isAccidentProduct() || this.isPaynowForGeneric()) && this.claim.getPolicyHolderType().toLowerCase()!=='c'){
            this.payNowForm.get('payNowOption').setValue('NRIC/FIN');
        }
    }

}
